import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { toMembershipSummary, toPaymentAccounts } from '../utils/organization-wire-utils';
import { dateOrNull, nullableBoolean, nullableNumber, nullableString } from '../utils/wire-value-utils';
import { BillingResponsibility } from './billing-responsibility.model';
import { BusinessType } from './business-type.model';
import { OrganizationMembershipSummary } from './organization-membership-summary.model';
import { OrganizationPaymentAccounts } from './organization-payment-accounts.model';
import { OrganizationType } from './organization-type.model';

/**
 * An organization as `/admin/organizations` returns it (hub `admin-organizations-controller.ts`), built to the
 * agreed `ADMIN_ORGANIZATION_LIST_READABLE` / `ADMIN_ORGANIZATION_READABLE` lists. The list sends a subset;
 * detail-only fields stay `undefined` on list rows. `legacyId` and the Zoho fields are deliberately not mapped.
 *
 * Tax ids: the detail sends `einLast4` / `ssnLast4` once the hub's response pass lands. Until the audited
 * tax-id endpoint exists, the full `ein` / `ssn` may still arrive on the detail and are read only by the edit
 * form. Display masked values from `einLast4` / `ssnLast4` (see `taxIdLast4`).
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays `null`).
 */
export class Organization extends Base {
  // Writable via PUT /admin/organizations/:id (the hub's ADMIN_ORGANIZATION_WRITABLE)
  name?: string | null;
  organizationType?: OrganizationType | null;
  ein?: string | null;
  ssn?: string | null;
  streetAddressLine1?: string | null;
  streetAddressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  email?: string | null;
  phone?: string | null;
  billingResponsibility?: BillingResponsibility | null;
  ownerProfileId?: string | null;
  chiefSupervisorProfileId?: string | null;
  setupComplete?: boolean | null;

  // Read-only
  businessType?: BusinessType | null;
  parentOrganizationId?: string | null;
  archivedAt: Date | null;
  serviceRadiusMiles?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  einLast4?: string | null;
  ssnLast4?: string | null;
  stripeCustomerId?: string | null;
  paymentMethodId?: string | null;
  archivedBy?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  /** Core/Pro summary; `null` when the hub could not read it from Stripe, `undefined` on list rows. */
  membership?: OrganizationMembershipSummary | null;
  paymentAccounts?: OrganizationPaymentAccounts;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.archivedAt = dateOrNull(source['archivedAt']);
    this.readWritableFields(source);
    this.readHubFields(source);
    this.membership = toMembershipSummary(source['membership']);
    this.paymentAccounts = toPaymentAccounts(source['paymentAccounts']);
  }

  private readWritableFields(source: Record<string, unknown>): void {
    this.name = nullableString(source['name']);
    this.organizationType = nullableString(source['organizationType']) as OrganizationType | null | undefined;
    this.ein = nullableString(source['ein']);
    this.ssn = nullableString(source['ssn']);
    this.streetAddressLine1 = nullableString(source['streetAddressLine1']);
    this.streetAddressLine2 = nullableString(source['streetAddressLine2']);
    this.city = nullableString(source['city']);
    this.state = nullableString(source['state']);
    this.zipCode = nullableString(source['zipCode']);
    this.country = nullableString(source['country']);
    this.email = nullableString(source['email']);
    this.phone = nullableString(source['phone']);
    this.billingResponsibility = nullableString(source['billingResponsibility']) as BillingResponsibility | null | undefined;
    this.ownerProfileId = nullableString(source['ownerProfileId']);
    this.chiefSupervisorProfileId = nullableString(source['chiefSupervisorProfileId']);
    this.setupComplete = nullableBoolean(source['setupComplete']);
  }

  private readHubFields(source: Record<string, unknown>): void {
    this.businessType = nullableString(source['businessType']) as BusinessType | null | undefined;
    this.parentOrganizationId = nullableString(source['parentOrganizationId']);
    this.serviceRadiusMiles = nullableNumber(source['serviceRadiusMiles']);
    this.latitude = nullableNumber(source['latitude']);
    this.longitude = nullableNumber(source['longitude']);
    this.einLast4 = nullableString(source['einLast4']);
    this.ssnLast4 = nullableString(source['ssnLast4']);
    this.stripeCustomerId = nullableString(source['stripeCustomerId']);
    this.paymentMethodId = nullableString(source['paymentMethodId']);
    this.archivedBy = nullableString(source['archivedBy']);
    this.createdBy = nullableString(source['createdBy']);
    this.updatedBy = nullableString(source['updatedBy']);
  }
}
